import React, { useState } from 'react';
import api from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { Button } from '../../../components/ui/Button';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

const IconStar = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const IconPencil = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const IconTrash = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconServer = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
  </svg>
);

const IconPlus = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

export interface SendIdentity {
  id: number;
  name: string;
  displayName: string | null;
  emailAddress: string | null;
  replyTo: string | null;
  isDefault: boolean;
  displayOrder: number;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpSecure?: boolean | null;
  smtpUsername?: string | null;
}

interface SendIdentitiesSectionProps {
  accountId: number;
  accountEmail: string;
  onMutate?: () => void;
}

export const SendIdentitiesSection: React.FC<SendIdentitiesSectionProps> = ({
  accountId,
  accountEmail,
  onMutate,
}) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [identities, setIdentities] = useState<SendIdentity[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SendIdentity | null>(null);
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    emailAddress: '',
    replyTo: '',
    isDefault: false,
    smtpHost: '',
    smtpPort: '',
    smtpSecure: true,
    smtpUsername: '',
    smtpPassword: '',
  });
  const [showSmtp, setShowSmtp] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<SendIdentity[]>(`/emails/accounts/${accountId}/send-identities`);
      setIdentities(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showError(typeof msg === 'string' ? msg : 'Failed to load send identities');
      setIdentities([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, showError]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      displayName: '',
      emailAddress: '',
      replyTo: '',
      isDefault: (identities?.length ?? 0) === 0,
      smtpHost: '',
      smtpPort: '',
      smtpSecure: true,
      smtpUsername: '',
      smtpPassword: '',
    });
    setShowSmtp(false);
    setModalOpen(true);
  };

  const openEdit = (id: SendIdentity) => {
    setEditing(id);
    setForm({
      name: id.name,
      displayName: id.displayName ?? '',
      emailAddress: id.emailAddress ?? '',
      replyTo: id.replyTo ?? '',
      isDefault: id.isDefault,
      smtpHost: id.smtpHost ?? '',
      smtpPort: id.smtpPort != null ? String(id.smtpPort) : '',
      smtpSecure: id.smtpSecure ?? true,
      smtpUsername: id.smtpUsername ?? '',
      smtpPassword: '', // leave empty; fill to change
    });
    setShowSmtp(!!id.smtpHost);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const smtpHost = form.smtpHost.trim();
      const smtpPayload = smtpHost
        ? {
            smtpHost,
            smtpPort: form.smtpPort ? parseInt(form.smtpPort, 10) : undefined,
            smtpSecure: form.smtpSecure,
            smtpUsername: form.smtpUsername.trim() || undefined,
            smtpPassword: form.smtpPassword ? form.smtpPassword : undefined, // only send when non-empty (edit: leave blank to keep current)
          }
        : { smtpHost: '' };
      if (editing) {
        await api.patch(`/emails/send-identities/${editing.id}`, {
          name: form.name.trim() || undefined,
          displayName: form.displayName.trim() || undefined,
          emailAddress: form.emailAddress.trim() || undefined,
          replyTo: form.replyTo.trim() || undefined,
          isDefault: form.isDefault,
          ...smtpPayload,
        });
        showSuccess('Send identity updated');
      } else {
        await api.post(`/emails/accounts/${accountId}/send-identities`, {
          name: form.name.trim(),
          displayName: form.displayName.trim() || undefined,
          emailAddress: form.emailAddress.trim() || undefined,
          replyTo: form.replyTo.trim() || undefined,
          isDefault: form.isDefault,
          ...smtpPayload,
        });
        showSuccess('Send identity created');
      }
      setModalOpen(false);
      load();
      onMutate?.();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showError(typeof msg === 'string' ? msg : (editing ? 'Failed to update' : 'Failed to create'));
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await api.patch(`/emails/send-identities/${id}/set-default`);
      showSuccess('Default identity updated');
      load();
      onMutate?.();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showError(typeof msg === 'string' ? msg : 'Failed to set default');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/emails/send-identities/${id}`);
      showSuccess('Send identity deleted');
      load();
      onMutate?.();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showError(typeof msg === 'string' ? msg : 'Failed to delete');
    }
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    await handleDelete(id);
  };

  if (loading && identities === null) {
    return (
      <div
        className={cn('py-4 text-sm', colors.textMuted)}
        role="status"
        aria-live="polite"
      >
        Loading send identities…
      </div>
    );
  }

  const list = identities ?? [];
  const hasIdentities = list.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div
          className={cn(
            'flex-1 min-w-[200px] rounded-lg border px-3 py-2.5 text-sm',
            colors.border,
            hasIdentities
              ? 'bg-emerald-50/80 text-emerald-950 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-100 dark:border-emerald-800'
              : 'bg-amber-50/90 text-amber-950 border-amber-200 dark:bg-amber-950/35 dark:text-amber-100 dark:border-amber-800'
          )}
          role="status"
          aria-live="polite"
        >
          <p className="font-semibold">
            {hasIdentities
              ? `${list.length} send identit${list.length === 1 ? 'y' : 'ies'} configured`
              : 'No send identities'}
          </p>
          <p className={cn('mt-1 text-xs leading-snug', hasIdentities ? 'text-emerald-900/90 dark:text-emerald-200/90' : 'text-amber-900/85 dark:text-amber-200/85')}>
            {hasIdentities
              ? 'Shared with everyone who has access to this account. One is marked as default for compose.'
              : "Compose will use this account's email address and display name only. Add an identity for alternate From or Reply-To."}
          </p>
        </div>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={openCreate}
          className="p-2 shrink-0"
          title="Add send identity"
          aria-label="Add send identity"
        >
          <IconPlus className="h-5 w-5" />
        </Button>
      </div>
      <p className={cn('text-sm', colors.textMuted)}>
        Optional: define how this account appears when sending. Identities are shared with all users who have access to this mailbox.
      </p>
      {!hasIdentities ? (
        <div
          className={cn(
            'rounded-lg border border-dashed px-4 py-6 text-center text-sm',
            colors.border,
            colors.textMuted
          )}
        >
          <p className="font-medium text-gray-700 dark:text-gray-300">None yet</p>
          <p className="mt-1">Use the + button above to create your first send identity.</p>
        </div>
      ) : (
        <div
          className={cn(
            'overflow-hidden rounded-xl border shadow-sm',
            colors.border,
            colors.surface
          )}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className={cn('border-b text-left text-xs font-semibold uppercase tracking-wide', colors.border, colors.textMuted)}>
                  <th className="py-3 pl-4 pr-2 w-[108px]">
                    <span className="sr-only">Actions</span>
                  </th>
                  <th className="py-3 px-2">Name</th>
                  <th className="py-3 px-2">Display name</th>
                  <th className="py-3 px-2">From</th>
                  <th className="py-3 px-2">Reply-To</th>
                  <th className="py-3 px-2 w-[120px]">SMTP</th>
                  <th className="py-3 pl-2 pr-4 w-14 text-center" title="Default for compose">
                    <span className="sr-only">Default</span>
                    <IconStar className="h-4 w-4 mx-auto opacity-70" />
                  </th>
                </tr>
              </thead>
              <tbody className={cn('divide-y', colors.border)}>
                {list.map((identity) => (
                  <tr
                    key={identity.id}
                    className={cn(
                      'transition-colors',
                      colors.text,
                      'hover:bg-gray-50/80 dark:hover:bg-gray-800/40'
                    )}
                  >
                    <td className="py-3 pl-4 pr-2">
                      <div className="flex items-center justify-start gap-0.5">
                        <button
                          type="button"
                          onClick={() => openEdit(identity)}
                          className={cn(
                            'rounded-lg p-2 transition-colors',
                            colors.textMuted,
                            'hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-300'
                          )}
                          title="Edit"
                          aria-label={`Edit ${identity.name}`}
                        >
                          <IconPencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(identity.id)}
                          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-100 hover:text-red-700 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                          title="Delete"
                          aria-label={`Delete ${identity.name}`}
                        >
                          <IconTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-2 font-medium">{identity.name}</td>
                    <td className="py-3 px-2 max-w-[140px]">
                      <span className="block truncate" title={identity.displayName || undefined}>
                        {identity.displayName || '—'}
                      </span>
                    </td>
                    <td className="py-3 px-2 max-w-[200px]">
                      <span
                        className={cn(
                          'inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          identity.emailAddress
                            ? 'bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-200 dark:border-indigo-800'
                            : 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600'
                        )}
                        title={identity.emailAddress || accountEmail}
                      >
                        <span className="truncate font-mono">{identity.emailAddress || accountEmail}</span>
                      </span>
                    </td>
                    <td className="py-3 px-2 max-w-[160px]">
                      <span className="block truncate text-xs" title={identity.replyTo || undefined}>
                        {identity.replyTo || '—'}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      {identity.smtpHost ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-200 dark:border-indigo-800"
                          title={identity.smtpHost}
                        >
                          <IconServer className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[5rem]">{identity.smtpHost}</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600">
                          Account
                        </span>
                      )}
                    </td>
                    <td className="py-3 pl-2 pr-4 text-center align-middle">
                      {identity.isDefault ? (
                        <span
                          className="inline-flex items-center justify-center rounded-full bg-amber-100 p-1.5 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          title="Default identity"
                        >
                          <IconStar className="h-4 w-4 fill-current" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(identity.id)}
                          className={cn(
                            'inline-flex items-center justify-center rounded-full p-1.5 transition-colors',
                            colors.textMuted,
                            'hover:bg-gray-200 hover:text-amber-600 dark:hover:bg-gray-700 dark:hover:text-amber-400'
                          )}
                          title="Set as default"
                          aria-label={`Set ${identity.name} as default identity`}
                        >
                          <IconStar className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalOpen(false)}>
          <div
            className={cn('rounded-lg shadow-xl p-6 max-w-md w-full mx-4', colors.surface, colors.border, 'border')}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">{editing ? 'Edit send identity' : 'Add send identity'}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Label (e.g. Work, Sales)</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={cn('w-full rounded border px-3 py-2 text-sm', colors.border, colors.text, 'bg-transparent')}
                  placeholder="Work"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Display name (From name)</label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  className={cn('w-full rounded border px-3 py-2 text-sm', colors.border, colors.text, 'bg-transparent')}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">From address (leave empty to use account address)</label>
                <input
                  type="email"
                  value={form.emailAddress}
                  onChange={(e) => setForm((f) => ({ ...f, emailAddress: e.target.value }))}
                  className={cn('w-full rounded border px-3 py-2 text-sm', colors.border, colors.text, 'bg-transparent')}
                  placeholder={accountEmail}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reply-To (optional)</label>
                <input
                  type="email"
                  value={form.replyTo}
                  onChange={(e) => setForm((f) => ({ ...f, replyTo: e.target.value }))}
                  className={cn('w-full rounded border px-3 py-2 text-sm', colors.border, colors.text, 'bg-transparent')}
                  placeholder=""
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={form.isDefault}
                  onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="isDefault" className="text-sm">Use as default when composing from this account</label>
              </div>
              <div className={cn('border-t pt-3 mt-3', colors.border)}>
                <button
                  type="button"
                  onClick={() => setShowSmtp(!showSmtp)}
                  className={cn('text-sm font-medium', colors.text, colors.interactiveHover)}
                >
                  {showSmtp ? '−' : '+'} SMTP settings (optional)
                </button>
                {showSmtp && (
                  <div className="mt-2 space-y-2 pl-0">
                    <p className={cn('text-xs', colors.textMuted)}>Leave empty to use the account&apos;s default SMTP.</p>
                    <div>
                      <label className="block text-xs font-medium mb-0.5">SMTP host</label>
                      <input
                        type="text"
                        value={form.smtpHost}
                        onChange={(e) => setForm((f) => ({ ...f, smtpHost: e.target.value }))}
                        className={cn('w-full rounded border px-2 py-1.5 text-sm', colors.border, colors.text, 'bg-transparent')}
                        placeholder="smtp.example.com"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium mb-0.5">Port</label>
                        <input
                          type="number"
                          value={form.smtpPort}
                          onChange={(e) => setForm((f) => ({ ...f, smtpPort: e.target.value }))}
                          className={cn('w-full rounded border px-2 py-1.5 text-sm', colors.border, colors.text, 'bg-transparent')}
                          placeholder="587"
                          min={1}
                          max={65535}
                        />
                      </div>
                      <div className="flex items-end pb-1.5">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={form.smtpSecure}
                            onChange={(e) => setForm((f) => ({ ...f, smtpSecure: e.target.checked }))}
                            className="rounded"
                          />
                          SSL/TLS
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-0.5">Username</label>
                      <input
                        type="text"
                        value={form.smtpUsername}
                        onChange={(e) => setForm((f) => ({ ...f, smtpUsername: e.target.value }))}
                        className={cn('w-full rounded border px-2 py-1.5 text-sm', colors.border, colors.text, 'bg-transparent')}
                        placeholder="SMTP username"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-0.5">Password {editing && '(leave blank to keep current)'}</label>
                      <input
                        type="password"
                        value={form.smtpPassword}
                        onChange={(e) => setForm((f) => ({ ...f, smtpPassword: e.target.value }))}
                        className={cn('w-full rounded border px-2 py-1.5 text-sm', colors.border, colors.text, 'bg-transparent')}
                        placeholder="SMTP password"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirmId != null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => void handleDeleteConfirm()}
        title="Delete send identity"
        message="Delete this send identity?"
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};
